import type { Spacing } from '@iheartjane/reefer';
import {
  Accordion,
  Box,
  Button,
  EditIcon,
  Flex,
  Link,
  Typography,
} from '@iheartjane/reefer';
import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { useCheckout } from '@jane/shared-ecomm/providers';
import { useJaneUser } from '@jane/shared/data-access';

import { CheckoutStep } from '../checkout.styles';
import { useCheckoutAccordion } from './CheckoutAccordionProvider';
import type { AccordionItems } from './CheckoutAccordionProvider';

import styles from '../checkout.module.css';

interface CheckoutAccordionItemProps {
  children: ReactNode;
  header: string;
  id: AccordionItems;
  mt?: Spacing;
  summary?: ReactNode;
}

export const CheckoutAccordionItem = ({
  children,
  header,
  id,
  mt,
  summary,
}: CheckoutAccordionItemProps) => {
  const {
    appInfo: { disableAuthFeatures },
    guestCheckout: { setShowLoginModal },
  } = useCheckout();
  const { data: janeUserData } = useJaneUser();
  const { expandableItems, expanded, setExpanded } = useCheckoutAccordion();

  const signedIn = Boolean(janeUserData?.user?.id);
  const showLogin = !signedIn && id === 'options' && !disableAuthFeatures;
  const isExpanded = expanded === id;

  useEffect(() => {
    if (isExpanded) {
      document.getElementById(`accordion-heading-${id}`)?.focus();
    }
  }, [isExpanded, id]);

  return (
    <CheckoutStep mt={mt} width="100%">
      <Accordion.Item id={id} expanded={isExpanded}>
        <Accordion.Heading
          className={isExpanded ? styles['accordionItem_expanded'] : undefined}
          aria-labelledby={`${id}-title`}
          onClick={!isExpanded ? () => setExpanded(id) : undefined}
          disabled={!expandableItems.includes(id)}
          aria-disabled={!expandableItems.includes(id) || isExpanded}
        >
          <Flex alignItems="center">
            <Typography id={`${id}-title`} variant="header-bold" branded>
              {header}
            </Typography>

            {(!isExpanded || showLogin) && (
              <Flex flexDirection="row" ml="auto">
                {showLogin && (
                  <Link
                    className={styles['noWrapLink']}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLoginModal(true);
                    }}
                    mr={isExpanded ? 0 : 16}
                    ml={16}
                    my="auto"
                    part="sign-in"
                  >
                    Sign in
                  </Link>
                )}

                {!isExpanded && (
                  <Button.Icon
                    disabled={!expandableItems.includes(id)}
                    variant="tertiary"
                    ml="auto"
                    icon={<EditIcon />}
                  />
                )}
              </Flex>
            )}
          </Flex>
          {!isExpanded && summary && <Box mt={24}>{summary}</Box>}
        </Accordion.Heading>
        <Accordion.Content>
          <Box mt={24}>{children}</Box>
        </Accordion.Content>
      </Accordion.Item>
    </CheckoutStep>
  );
};
